/**
 * Word parent tools — schemas and Office.js handler implementations.
 *
 * Each tool:
 *  1. Has a `schema` (ParentToolSchema) that is registered with the Usable embed.
 *  2. Has a `handler` that executes the Office.js operation and returns a plain object.
 *
 * All handlers wrap operations in Word.run() and call context.sync().
 * Read operations use .load() before sync and read values after.
 */

import {
  ParentToolSchema,
  executeToolCall,
  LONG_TOOL_TIMEOUT_MS,
  __resetToolExecutionForTests,
} from "@usable/office-embed";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type WordToolHandler = (args: Record<string, unknown>) => Promise<unknown>;

export interface WordTool {
  schema: ParentToolSchema;
  handler: WordToolHandler;
  /**
   * Minimum WordApi requirement set this tool needs (e.g. "1.3"). When set, the
   * tool is only advertised — and only callable — on hosts where
   * `Office.context.requirements.isSetSupported("WordApi", minRequirementSet)`
   * is true. Untagged tools are always available (they rely on WordApi ≥ 1.1,
   * the baseline every modern Word host supports). See AD e75bd8dd (over-routing)
   * and the Word Power Tools PRD §5.2 for why gating matters.
   */
  minRequirementSet?: string;
}

/**
 * Predicate matching `Office.context.requirements.isSetSupported`. Factored out
 * so the support-filter is unit-testable without a live Office host.
 */
export type IsSetSupported = (name: string, minVersion?: string) => boolean;

// ---------------------------------------------------------------------------
// Parameter mapping helpers (WT1)
//
// These are pure functions over plain args + an `isSetSupported` predicate, kept
// separate from the Office.js handlers so the schema→Office mapping is unit-
// testable without a live host. Properties that need a requirement set beyond the
// WordApi 1.1 baseline are gated per-property here (rather than gating the whole
// tool, as WT0 does) — the tool stays advertised everywhere, and any prop the
// host can't honour is reported back in `unsupported` instead of crashing.
// ---------------------------------------------------------------------------

/** The plain search-options bag accepted by `Word.Body.search` (all WordApi 1.1). */
export interface WordSearchOptions {
  matchCase?: boolean;
  matchWholeWord?: boolean;
  matchWildcards?: boolean;
  matchPrefix?: boolean;
  matchSuffix?: boolean;
  ignorePunct?: boolean;
  ignoreSpace?: boolean;
}

const SEARCH_FLAGS: (keyof WordSearchOptions)[] = [
  "matchCase",
  "matchWholeWord",
  "matchWildcards",
  "matchPrefix",
  "matchSuffix",
  "ignorePunct",
  "ignoreSpace",
];

/** Build a search-options bag from only the boolean flags actually provided. */
export function buildSearchOptions(args: Record<string, unknown>): WordSearchOptions {
  const options: WordSearchOptions = {};
  for (const flag of SEARCH_FLAGS) {
    if (typeof args[flag] === "boolean") options[flag] = args[flag] as boolean;
  }
  return options;
}

type AlignmentLiteral = "Left" | "Centered" | "Right" | "Justified";
const ALIGNMENT_MAP: Record<string, AlignmentLiteral> = {
  left: "Left",
  centered: "Centered",
  right: "Right",
  justified: "Justified",
};

/**
 * Map a friendly alignment string to the Word.Alignment string-enum literal, or
 * undefined if absent/unknown. Returns the literal (not `Word.Alignment.*`) so it
 * is safe to call before Office.js has loaded the `Word` global.
 */
export function resolveAlignment(value: unknown): AlignmentLiteral | undefined {
  if (typeof value !== "string") return undefined;
  return ALIGNMENT_MAP[value];
}

/** Requirement set a property needs beyond the 1.1 baseline. */
interface PropGate {
  name: string;
  version: string;
}

/** Word.Font props beyond the 1.1 baseline that need gating (set name + version). */
const GATED_FONT_PROPS: Record<string, PropGate> = {
  hidden: { name: "WordApiDesktop", version: "1.2" },
  allCaps: { name: "WordApiDesktop", version: "1.3" },
  smallCaps: { name: "WordApiDesktop", version: "1.3" },
};

/** Every Word.Font property we expose (1.1 baseline + gated). Order is stable for tests. */
const FONT_PROPS = [
  "name",
  "size",
  "color",
  "bold",
  "italic",
  "underline",
  "highlightColor",
  "strikeThrough",
  "doubleStrikeThrough",
  "subscript",
  "superscript",
  "allCaps",
  "smallCaps",
  "hidden",
] as const;

export interface PropAssignment {
  prop: string;
  value: unknown;
}
export interface UnsupportedProp {
  property: string;
  requiredSet: string;
}
export interface CollectedAssignments {
  assignments: PropAssignment[];
  unsupported: UnsupportedProp[];
}

/**
 * Collect the Word.Font assignments to apply from the provided args. Only props
 * actually present are applied (so a partial schema like `format_text`'s works
 * through the same function as `set_font`). Gated props the host can't support
 * are diverted into `unsupported` so the caller can report them truthfully.
 */
export function collectFontAssignments(
  args: Record<string, unknown>,
  isSetSupported: IsSetSupported
): CollectedAssignments {
  const assignments: PropAssignment[] = [];
  const unsupported: UnsupportedProp[] = [];
  for (const prop of FONT_PROPS) {
    if (args[prop] === undefined) continue;
    const gate = GATED_FONT_PROPS[prop];
    if (gate && !isSetSupported(gate.name, gate.version)) {
      unsupported.push({ property: prop, requiredSet: `${gate.name} ${gate.version}` });
      continue;
    }
    assignments.push({ prop, value: args[prop] });
  }
  return { assignments, unsupported };
}

export interface InvalidProp {
  property: string;
  message: string;
}
export interface NormalizedFontAssignments {
  assignments: PropAssignment[];
  invalid: InvalidProp[];
}

/**
 * Normalize a color value for `Word.Font.color` / `highlightColor`. Accepts a
 * `#RGB` / `#RRGGBB` hex (with or without the leading `#`, which the model often
 * omits) and returns it canonicalised; passes a bare colour *name* through for
 * Office to resolve; rejects anything else. Pure.
 */
export function normalizeColorValue(value: unknown): { value?: string; error?: string } {
  if (typeof value !== "string" || value.trim() === "") {
    return { error: "must be a non-empty string (hex like '#FF0000' or a colour name)." };
  }
  const v = value.trim();
  const hex = v.startsWith("#") ? v : `#${v}`;
  if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(hex)) return { value: hex.toUpperCase() };
  if (/^[a-zA-Z]+$/.test(v)) return { value: v }; // a colour name — let Office validate it
  return { error: `'${value}' is not a valid hex colour or colour name.` };
}

/** The canonical Word.UnderlineType members (what Office.js accepts, verbatim). */
const UNDERLINE_TYPES = [
  "Mixed", "None", "Hidden", "DotLine", "Single", "Word", "Double", "Thick",
  "Dotted", "DottedHeavy", "Dash", "DashHeavy", "DashLong", "DashLongHeavy",
  "DotDash", "DotDashHeavy", "DotDotDash", "DotDotDashHeavy", "Wave",
  "WaveHeavy", "WaveDouble",
] as const;
const UNDERLINE_BY_LOWER = new Map(UNDERLINE_TYPES.map((t) => [t.toLowerCase(), t]));
/** Boolean-ish strings the model tends to emit for a string-typed underline field. */
const UNDERLINE_TRUTHY = new Set(["true", "1", "yes", "on"]);
const UNDERLINE_FALSY = new Set(["false", "0", "no", "off"]);

/**
 * Normalize an `underline` value to a canonical `Word.UnderlineType` literal.
 * Accepts a real boolean (`true`→Single / `false`→None), the boolean-ish strings
 * a model emits when the schema types the field as a string (`"true"`, `"off"`,
 * …), and any case-insensitive underline-type name. Rejects anything else with a
 * structured error rather than letting Office throw an opaque InvalidArgument —
 * the exact failure the Word stress test hit on `underline: true`. Pure.
 */
export function normalizeUnderlineValue(value: unknown): { value?: string; error?: string } {
  if (typeof value === "boolean") return { value: value ? "Single" : "None" };
  if (typeof value !== "string" || value.trim() === "") {
    return { error: "must be an underline style like 'Single' or 'None' (or a boolean)." };
  }
  const key = value.trim().toLowerCase();
  if (UNDERLINE_TRUTHY.has(key)) return { value: "Single" };
  if (UNDERLINE_FALSY.has(key)) return { value: "None" };
  const canonical = UNDERLINE_BY_LOWER.get(key);
  if (canonical) return { value: canonical };
  return { error: `'${value}' is not a valid underline style.` };
}

/**
 * Coerce the raw font assignments into what Office.js will actually accept,
 * diverting anything invalid into `invalid` (with the offending property named)
 * so the caller can fail fast with an actionable message instead of letting
 * Office throw an opaque `InvalidArgument`. The model slips this guards against:
 *  - `underline` as a boolean or boolean-ish/loose-cased string → the canonical
 *    `Word.UnderlineType` literal the API wants (or a named error).
 *  - `color` / `highlightColor` given without a leading `#` (or as a name).
 * Every other prop passes through untouched. Pure.
 */
export function normalizeFontAssignments(
  assignments: PropAssignment[]
): NormalizedFontAssignments {
  const out: PropAssignment[] = [];
  const invalid: InvalidProp[] = [];
  for (const { prop, value } of assignments) {
    if (prop === "underline") {
      const { value: normalized, error } = normalizeUnderlineValue(value);
      if (error) invalid.push({ property: prop, message: `underline ${error}` });
      else out.push({ prop, value: normalized });
      continue;
    }
    if (prop === "color" || prop === "highlightColor") {
      const { value: normalized, error } = normalizeColorValue(value);
      if (error) invalid.push({ property: prop, message: `${prop} ${error}` });
      else out.push({ prop, value: normalized });
      continue;
    }
    out.push({ prop, value });
  }
  return { assignments: out, invalid };
}

/** Direct Word.Paragraph numeric props — all WordApi 1.1. */
const PARAGRAPH_DIRECT_PROPS = [
  "lineSpacing",
  "spaceBefore",
  "spaceAfter",
  "leftIndent",
  "rightIndent",
  "firstLineIndent",
] as const;

/**
 * `keepWithNext` / `keepTogether` live on `ParagraphFormat`, reached via
 * `Range.paragraphFormat` (WordApiDesktop 1.4) — gate on that accessor.
 */
const PARAGRAPH_FORMAT_GATE: PropGate = { name: "WordApiDesktop", version: "1.4" };
const PARAGRAPH_FORMAT_PROPS = ["keepWithNext", "keepTogether"] as const;

export interface CollectedParagraphAssignments {
  /** Numeric props set directly on the paragraph. */
  direct: PropAssignment[];
  /** Boolean props set on the paragraph's range `paragraphFormat`. */
  format: PropAssignment[];
  unsupported: UnsupportedProp[];
}

/** Collect Word.Paragraph + ParagraphFormat assignments (alignment handled separately). */
export function collectParagraphAssignments(
  args: Record<string, unknown>,
  isSetSupported: IsSetSupported
): CollectedParagraphAssignments {
  const direct: PropAssignment[] = [];
  const format: PropAssignment[] = [];
  const unsupported: UnsupportedProp[] = [];

  for (const prop of PARAGRAPH_DIRECT_PROPS) {
    if (typeof args[prop] === "number") direct.push({ prop, value: args[prop] });
  }
  for (const prop of PARAGRAPH_FORMAT_PROPS) {
    if (typeof args[prop] !== "boolean") continue;
    if (!isSetSupported(PARAGRAPH_FORMAT_GATE.name, PARAGRAPH_FORMAT_GATE.version)) {
      unsupported.push({
        property: prop,
        requiredSet: `${PARAGRAPH_FORMAT_GATE.name} ${PARAGRAPH_FORMAT_GATE.version}`,
      });
      continue;
    }
    format.push({ prop, value: args[prop] });
  }
  return { direct, format, unsupported };
}

/** True when the caller asked to target paragraphs by index or text match. */
export function hasParagraphTargeting(args: Record<string, unknown>): boolean {
  return (
    (Array.isArray(args.paragraphIndices) && args.paragraphIndices.length > 0) ||
    (typeof args.match === "string" && args.match !== "")
  );
}

/**
 * Resolve which paragraph indices a tool should act on, given each paragraph's
 * text and the targeting args. Indices come first (in the given order, deduped),
 * then any further paragraphs whose text contains `match` (case-insensitive).
 * Out-of-range / non-integer indices are ignored. Pure — no Office dependency.
 */
export function selectParagraphIndices(
  paragraphTexts: string[],
  args: Record<string, unknown>
): number[] {
  const out: number[] = [];
  const push = (i: number) => {
    if (!out.includes(i)) out.push(i);
  };

  if (Array.isArray(args.paragraphIndices)) {
    for (const raw of args.paragraphIndices) {
      const i = Number(raw);
      if (Number.isInteger(i) && i >= 0 && i < paragraphTexts.length) push(i);
    }
  }
  if (typeof args.match === "string" && args.match !== "") {
    const needle = (args.match as string).toLowerCase().trim();
    // `matchMode: "exact"` matches a whole paragraph (case-insensitive, trimmed)
    // rather than any paragraph that merely *contains* the phrase. The default
    // stays substring for back-compat, but exact is what stops a title-style
    // request from also styling a body paragraph that quotes the title (the
    // over-application seen in the Word stress test).
    const exact = args.matchMode === "exact";
    paragraphTexts.forEach((text, i) => {
      const hay = text.toLowerCase().trim();
      if (exact ? hay === needle : hay.includes(needle)) push(i);
    });
  }
  return out;
}

/**
 * When a targeting-based tool matched no paragraphs, return a `note` explaining
 * the most likely cause: stale paragraphIndices / match. Paragraph indices shift
 * after every insertion, so indices read early in a long editing session point
 * elsewhere later — the tool then silently applies to nothing and returns
 * `appliedTo: 0`. Surfacing a note (instead of a bare success) is what lets the
 * model notice and re-read `get_paragraphs`. Only fires when targeting was asked
 * for — an empty selection is the caller's business, not a stale-index signal.
 */
export function staleTargetNote(
  args: Record<string, unknown>,
  appliedTo: number
): { note?: string } {
  if (appliedTo === 0 && hasParagraphTargeting(args)) {
    return {
      note:
        "No paragraphs matched the given paragraphIndices / match — they are likely stale " +
        "(paragraph indices shift after inserts). Re-read get_paragraphs and retry.",
    };
  }
  return {};
}

// ---------------------------------------------------------------------------
// Rich media & links helpers (WT2)
// ---------------------------------------------------------------------------

/** ~9M base64 chars ≈ 6.7 MB decoded — a sane ceiling that still allows real images. */
export const MAX_IMAGE_BASE64_LENGTH = 9_000_000;

export interface Base64Result {
  base64?: string;
  error?: string;
}

/**
 * Normalize image input for `insertInlinePictureFromBase64`: strip an optional
 * `data:<mime>;base64,` prefix, reject empty / oversized / non-base64 payloads.
 * Pure — unit-testable without Office.
 */
export function normalizeImageBase64(
  input: unknown,
  maxLength: number = MAX_IMAGE_BASE64_LENGTH
): Base64Result {
  if (typeof input !== "string" || input.trim() === "") {
    return { error: "image is required: pass non-empty base64 image data." };
  }
  const trimmed = input.trim();
  const comma = trimmed.indexOf(",");
  const payload = trimmed.startsWith("data:") && comma !== -1 ? trimmed.slice(comma + 1) : trimmed;
  const base64 = payload.replace(/\s+/g, "");
  if (base64 === "") {
    return { error: "image base64 is empty after stripping the data-URL prefix." };
  }
  if (base64.length > maxLength) {
    return { error: `image is too large (${base64.length} base64 chars; max ${maxLength}).` };
  }
  if (!/^[A-Za-z0-9+/]+={0,2}$/.test(base64)) {
    return { error: "image data is not valid base64." };
  }
  return { base64 };
}

export interface UrlResult {
  url?: string;
  error?: string;
}

/**
 * Validate a hyperlink address. Accepts http(s), mailto, same-document anchors
 * (`#name`), and site-relative paths (`/path`). Pure.
 */
export function normalizeHyperlinkUrl(input: unknown): UrlResult {
  if (typeof input !== "string" || input.trim() === "") {
    return { error: "hyperlink address (url) is required." };
  }
  const url = input.trim();
  if (!/^(https?:\/\/|mailto:|#|\/)/i.test(url)) {
    return { error: "hyperlink address must start with http://, https://, mailto:, # or /." };
  }
  return { url };
}

// ---------------------------------------------------------------------------
// List helpers (WT3)
// ---------------------------------------------------------------------------

type BulletLiteral = "Solid" | "Hollow" | "Square" | "Diamonds" | "Arrow" | "Checkmark";
const BULLET_ALIASES: Record<string, BulletLiteral> = {
  solid: "Solid",
  disc: "Solid",
  filled: "Solid",
  hollow: "Hollow",
  circle: "Hollow",
  open: "Hollow",
  square: "Square",
  diamond: "Diamonds",
  diamonds: "Diamonds",
  arrow: "Arrow",
  checkmark: "Checkmark",
  check: "Checkmark",
  tick: "Checkmark",
};

/** Map a friendly bullet name to a Word.ListBullet literal; defaults to Solid. */
export function resolveListBullet(style: unknown): BulletLiteral {
  if (typeof style === "string") {
    const key = style.toLowerCase().replace(/[\s_-]/g, "");
    if (BULLET_ALIASES[key]) return BULLET_ALIASES[key];
  }
  return "Solid";
}

type NumberingLiteral = "Arabic" | "UpperRoman" | "LowerRoman" | "UpperLetter" | "LowerLetter";
const NUMBERING_ALIASES: Record<string, NumberingLiteral> = {
  arabic: "Arabic",
  decimal: "Arabic",
  number: "Arabic",
  numbers: "Arabic",
  numbered: "Arabic",
  upperroman: "UpperRoman",
  lowerroman: "LowerRoman",
  upperletter: "UpperLetter",
  alpha: "UpperLetter",
  lowerletter: "LowerLetter",
  loweralpha: "LowerLetter",
};

/** Map a friendly numbering name to a Word.ListNumbering literal; defaults to Arabic. */
export function resolveListNumbering(style: unknown): NumberingLiteral {
  if (typeof style === "string") {
    const key = style.toLowerCase().replace(/[\s_-]/g, "");
    if (NUMBERING_ALIASES[key]) return NUMBERING_ALIASES[key];
  }
  return "Arabic";
}

export const MAX_LIST_LEVEL = 8;
export interface ListLevelResult {
  level?: number;
  error?: string;
}

/** Validate a 0-based list indent level (0–8). Pure. */
export function resolveListLevel(input: unknown): ListLevelResult {
  const n = Number(input);
  if (!Number.isInteger(n) || n < 0 || n > MAX_LIST_LEVEL) {
    return { error: `level must be an integer between 0 and ${MAX_LIST_LEVEL}.` };
  }
  return { level: n };
}

// ---------------------------------------------------------------------------
// Comment helpers (WT5)
// ---------------------------------------------------------------------------

/** Validate a comment id (as surfaced by get_comments). Pure. */
export function validateCommentId(input: unknown): { id?: string; error?: string } {
  if (typeof input !== "string" || input.trim() === "") {
    return { error: "commentId is required — get it from get_comments." };
  }
  return { id: input.trim() };
}

export interface CommentReplyDTO {
  author: string;
  text: string;
}
export interface CommentDTO {
  id: string;
  author: string;
  text: string;
  resolved: boolean;
  created?: string;
  replies: CommentReplyDTO[];
}

/** The subset of a loaded Word.Comment this maps from (also matches the test fixtures). */
export interface RawComment {
  id: string;
  authorName: string;
  content: string;
  resolved: boolean;
  creationDate?: Date | string;
  replies?: { items?: { authorName: string; content: string }[] };
}

function toIsoDate(value: unknown): string | undefined {
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "string" && value !== "") return value;
  return undefined;
}

/** Map a loaded comment to the flat DTO returned by get_comments. Pure. */
export function mapComment(raw: RawComment): CommentDTO {
  return {
    id: raw.id,
    author: raw.authorName,
    text: raw.content,
    resolved: raw.resolved,
    created: toIsoDate(raw.creationDate),
    replies: (raw.replies?.items ?? []).map((r) => ({ author: r.authorName, text: r.content })),
  };
}

// ---------------------------------------------------------------------------
// Structure & navigation helpers (WT7)
// ---------------------------------------------------------------------------

/** Word's own bookmark-name length limit. */
export const MAX_BOOKMARK_NAME_LENGTH = 40;

/**
 * Validate a Word bookmark name: starts with a letter, then letters/digits/
 * underscores, max 40 chars (Word's own constraint). Pure.
 */
export function validateBookmarkName(input: unknown): { name?: string; error?: string } {
  if (typeof input !== "string" || input.trim() === "") {
    return { error: "bookmark name is required." };
  }
  const name = input.trim();
  if (name.length > MAX_BOOKMARK_NAME_LENGTH) {
    return { error: `bookmark name must be ${MAX_BOOKMARK_NAME_LENGTH} characters or fewer.` };
  }
  if (!/^[A-Za-z][A-Za-z0-9_]*$/.test(name)) {
    return {
      error: "bookmark name must start with a letter and contain only letters, digits, or underscores.",
    };
  }
  return { name };
}

export type HeaderFooterLiteral = "Primary" | "FirstPage" | "EvenPages";

/** Map a friendly header/footer target to the Word.HeaderFooterType literal; defaults to Primary. */
export function resolveHeaderFooterType(input: unknown): HeaderFooterLiteral {
  if (typeof input === "string") {
    const key = input.toLowerCase().replace(/[\s_-]/g, "");
    if (key === "firstpage" || key === "first") return "FirstPage";
    if (key === "evenpages" || key === "even") return "EvenPages";
  }
  return "Primary";
}

// ---------------------------------------------------------------------------
// Table helpers (WT4)
//
// Pure argument mappers for the advanced-table tools — index/count validation
// and insert-location mapping — kept host-free so they can be unit-tested
// without a live Word.
// ---------------------------------------------------------------------------

/** `Table.addRows`/`addColumns` only accept the Start or End edge (not Before/After). */
export type TableEdgeLiteral = "Start" | "End";

/**
 * Map a friendly position to the Start/End edge `addRows`/`addColumns` accept.
 * Anything meaning top/left/beginning → Start; everything else → End (append).
 */
export function resolveTableEdge(input: unknown): TableEdgeLiteral {
  if (typeof input === "string") {
    const key = input.toLowerCase().replace(/[\s_-]/g, "");
    if (["start", "beginning", "top", "before", "left"].includes(key)) return "Start";
  }
  return "End";
}

/** Validate a positive-integer count (rows/columns to add). Pure. */
export function validatePositiveCount(input: unknown): { count?: number; error?: string } {
  if (typeof input !== "number" || !Number.isInteger(input) || input < 1) {
    return { error: "count must be a positive integer." };
  }
  return { count: input };
}

/** Validate a 0-based index (table/row/column). Pure. */
export function validateTableIndex(
  input: unknown,
  label: string
): { index?: number; error?: string } {
  if (typeof input !== "number" || !Number.isInteger(input) || input < 0) {
    return { error: `${label} must be a non-negative integer.` };
  }
  return { index: input };
}

/** The subset of `Word.Table` style props `set_table_style` writes (all WordApi 1.3). */
export interface TableStyleAssignments {
  styleBuiltIn?: string;
  headerRowCount?: number;
  styleBandedRows?: boolean;
  styleBandedColumns?: boolean;
  styleFirstColumn?: boolean;
  styleLastColumn?: boolean;
  styleTotalRow?: boolean;
}

/** Friendly arg name → the boolean `Word.Table` style prop it drives. */
const TABLE_STYLE_FLAGS: Record<string, keyof TableStyleAssignments> = {
  bandedRows: "styleBandedRows",
  bandedColumns: "styleBandedColumns",
  firstColumn: "styleFirstColumn",
  lastColumn: "styleLastColumn",
  totalRow: "styleTotalRow",
};

/**
 * Collect only the table-style assignments actually present in `args`:
 * `styleName` → the built-in style, `headerRow` → the header-row count (1/0),
 * and the remaining booleans → their `style*` props. Pure.
 */
export function collectTableStyleAssignments(
  args: Record<string, unknown>
): TableStyleAssignments {
  const out: TableStyleAssignments = {};
  if (typeof args.styleName === "string" && args.styleName.trim() !== "") {
    out.styleBuiltIn = args.styleName.trim();
  }
  if (typeof args.headerRow === "boolean") {
    out.headerRowCount = args.headerRow ? 1 : 0;
  }
  for (const [arg, prop] of Object.entries(TABLE_STYLE_FLAGS)) {
    if (typeof args[arg] === "boolean") {
      (out[prop] as boolean) = args[arg] as boolean;
    }
  }
  return out;
}

/**
 * Column count of a table from its 2-D `values`: the max cells across rows, so
 * a merged row (which has fewer cells) doesn't under-count the table. Pure.
 */
export function tableColumnCount(values: string[][]): number {
  return values.reduce((max, row) => Math.max(max, row.length), 0);
}

// ---------------------------------------------------------------------------
// Track-changes helpers (WT6)
// ---------------------------------------------------------------------------

/** The `Document.changeTrackingMode` values Word accepts (WordApi 1.4). */
export type ChangeTrackingModeLiteral = "Off" | "TrackAll" | "TrackMineOnly";

/** Friendly, normalised input token → the `changeTrackingMode` Word expects. */
const CHANGE_TRACKING_MODE_ALIASES: Record<string, ChangeTrackingModeLiteral> = {
  off: "Off",
  false: "Off",
  none: "Off",
  stop: "Off",
  disable: "Off",
  disabled: "Off",
  trackall: "TrackAll",
  all: "TrackAll",
  on: "TrackAll",
  true: "TrackAll",
  everyone: "TrackAll",
  trackmineonly: "TrackMineOnly",
  mineonly: "TrackMineOnly",
  mine: "TrackMineOnly",
  me: "TrackMineOnly",
  myown: "TrackMineOnly",
};

/**
 * Resolve a caller-supplied mode to a `Document.changeTrackingMode` value.
 * Case-insensitive; ignores spaces / `_` / `-` and accepts friendly aliases
 * ("on" / "all" → TrackAll, "mine" → TrackMineOnly, "off" → Off). Returns
 * `{ error }` (never throws) so the handler can surface it before `Word.run`. Pure.
 */
export function resolveChangeTrackingMode(
  input: unknown
): { mode?: ChangeTrackingModeLiteral; error?: string } {
  if (typeof input !== "string") {
    return { error: "mode must be one of: off, trackAll, trackMineOnly." };
  }
  const key = input.toLowerCase().replace(/[\s_-]/g, "");
  const mode = CHANGE_TRACKING_MODE_ALIASES[key];
  if (!mode) {
    return { error: `Unknown track-changes mode "${input}". Use one of: off, trackAll, trackMineOnly.` };
  }
  return { mode };
}

/** Max characters of a tracked change's text returned by `get_tracked_changes`. */
export const TRACKED_TEXT_PREVIEW_MAX = 1000;

/** Default cap on how many tracked changes `get_tracked_changes` returns. */
export const TRACKED_CHANGES_DEFAULT_LIMIT = 200;

/** A tracked change as returned to the AI (plain, serialisable). */
export interface TrackedChangeDTO {
  type: string;
  author: string;
  date: string;
  text: string;
}

/**
 * Shape a loaded `Word.TrackedChange` (or its `toJSON` form) into a plain DTO:
 * normalises the date to an ISO string and truncates long text to
 * `TRACKED_TEXT_PREVIEW_MAX` with a trailing ellipsis. Defensive about missing
 * fields so a partially-loaded proxy never throws. Pure.
 */
export function mapTrackedChange(raw: {
  type?: unknown;
  author?: unknown;
  date?: unknown;
  text?: unknown;
}): TrackedChangeDTO {
  const rawText = typeof raw.text === "string" ? raw.text : "";
  const text =
    rawText.length > TRACKED_TEXT_PREVIEW_MAX
      ? rawText.slice(0, TRACKED_TEXT_PREVIEW_MAX) + "…"
      : rawText;

  let date = "";
  if (raw.date instanceof Date && !isNaN(raw.date.getTime())) {
    date = raw.date.toISOString();
  } else if (typeof raw.date === "string") {
    date = raw.date;
  }

  return {
    type: typeof raw.type === "string" ? raw.type : "Unknown",
    author: typeof raw.author === "string" ? raw.author : "",
    date,
    text,
  };
}

// ---------------------------------------------------------------------------
// Runtime targeting helper (WT1 / folds task 6afd0646)
//
// Formatting tools historically acted only on the live selection, so the AI
// could style at most one paragraph and would silently under-apply bulk requests
// ("make all headings Heading 1"). These helpers let a tool target paragraphs by
// index and/or text match, falling back to the selection for back-compat.
// ---------------------------------------------------------------------------

/**
 * Resolve the paragraphs a formatting tool should act on:
 *  - `paragraphIndices` / `match` given → those body paragraphs (see
 *    `selectParagraphIndices`).
 *  - neither given → the paragraphs intersecting the current selection (the
 *    original behaviour).
 * Returns loaded `Word.Paragraph` proxies in the live context.
 */
async function resolveTargetParagraphs(
  context: Word.RequestContext,
  args: Record<string, unknown>
): Promise<Word.Paragraph[]> {
  if (hasParagraphTargeting(args)) {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("items/text");
    await context.sync();
    const texts = paragraphs.items.map((p) => p.text);
    return selectParagraphIndices(texts, args).map((i) => paragraphs.items[i]);
  }

  const selectionParagraphs = context.document.getSelection().paragraphs;
  selectionParagraphs.load("items");
  await context.sync();
  return selectionParagraphs.items;
}

/** Apply collected font assignments to a Word.Font proxy (props applied as-is). */
function applyFontAssignments(font: Word.Font, assignments: PropAssignment[]): void {
  for (const { prop, value } of assignments) {
    (font as unknown as Record<string, unknown>)[prop] = value;
  }
}

/**
 * Shared implementation for the font tools (`format_text`, `set_font`). Applies
 * the provided font props to either the targeted paragraphs (when targeting args
 * are present) or the live selection (back-compat). Reports `appliedTo` when
 * targeting and any host-`unsupported` props.
 */
async function applyFontTool(args: Record<string, unknown>): Promise<unknown> {
  const collected = collectFontAssignments(args, defaultIsSetSupported);
  const { assignments, invalid } = normalizeFontAssignments(collected.assignments);
  const unsupported = collected.unsupported;
  // Fail fast on a bad value (naming the property) rather than letting Office
  // throw an opaque InvalidArgument that the model can't act on. Nothing is
  // written, so the document state stays clean for a corrected retry.
  if (invalid.length) {
    return { error: `Invalid font value(s): ${invalid.map((i) => i.message).join("; ")}`, invalid };
  }
  return Word.run(async (context) => {
    let appliedTo: number | undefined;
    if (hasParagraphTargeting(args)) {
      const targets = await resolveTargetParagraphs(context, args);
      targets.forEach((p) => applyFontAssignments(p.font, assignments));
      appliedTo = targets.length;
    } else {
      applyFontAssignments(context.document.getSelection().font, assignments);
    }
    await context.sync();
    return {
      success: true,
      ...(appliedTo !== undefined ? { appliedTo } : {}),
      ...(unsupported.length ? { unsupported } : {}),
      ...(appliedTo !== undefined ? staleTargetNote(args, appliedTo) : {}),
    };
  });
}

/**
 * Shared implementation for the paragraph-format tools (`set_paragraph_format`
 * and its `set_paragraph_alignment` alias). Applies alignment + spacing/indent
 * directly to each target paragraph, and keep-with-next/keep-together via the
 * paragraph's range `paragraphFormat` (gated). Targets by index/match or the
 * selection. Always returns a truthful `appliedTo` count.
 */
async function applyParagraphFormatTool(args: Record<string, unknown>): Promise<unknown> {
  const alignment = resolveAlignment(args.alignment);
  const { direct, format, unsupported } = collectParagraphAssignments(args, defaultIsSetSupported);
  return Word.run(async (context) => {
    const targets = await resolveTargetParagraphs(context, args);
    targets.forEach((p) => {
      if (alignment !== undefined) p.alignment = alignment;
      for (const { prop, value } of direct) {
        (p as unknown as Record<string, unknown>)[prop] = value;
      }
      if (format.length > 0) {
        // `Range.paragraphFormat` (keepWithNext/keepTogether) postdates the pinned
        // @types/office-js, but the CDN runtime exposes it — and this branch only
        // runs when WordApiDesktop 1.4 is supported (see collectParagraphAssignments).
        const paragraphFormat = (p.getRange() as unknown as {
          paragraphFormat: Record<string, unknown>;
        }).paragraphFormat;
        for (const { prop, value } of format) {
          paragraphFormat[prop] = value;
        }
      }
    });
    await context.sync();
    return {
      success: true,
      appliedTo: targets.length,
      ...(unsupported.length ? { unsupported } : {}),
      ...staleTargetNote(args, targets.length),
    };
  });
}

/**
 * Shared implementation for the list tools (`apply_bullets` / `apply_numbering`).
 * Turns the target paragraphs into a single list: the first paragraph starts a
 * new list, the rest attach to it at level 0, then `configure(list)` sets the
 * level's bullet or numbering. Targets by paragraphIndices/match, or the current
 * selection when neither is given. Returns the number of paragraphs listed.
 *
 * Targeting matters here: without it the agent has no way to select several
 * paragraphs at once, so each call made its own one-item list (each restarting
 * at "1"). Passing all targets to a single startNewList keeps them one 1-2-3 list.
 */
async function applyListToTargets(
  args: Record<string, unknown>,
  configure: (list: Word.List) => void
): Promise<unknown> {
  return Word.run(async (context) => {
    const targets = await resolveTargetParagraphs(context, args);
    if (targets.length === 0) {
      return {
        error: "No paragraphs to list — select some, or pass paragraphIndices / match.",
      };
    }

    // Load list membership for EVERY target, not just the first. `startNewList()`
    // and `attachToList()` throw a GeneralException when handed a paragraph that
    // already belongs to a list, so a single already-listed target anywhere in
    // the range used to blow up the whole call (the apply_numbering failure in
    // the Word stress test, where earlier list ops had listed some paragraphs).
    targets.forEach((p) => p.listOrNullObject.load("id,isNullObject"));
    await context.sync();

    try {
      // Reuse a list any target already belongs to; otherwise seed a fresh one
      // from the first target. Then attach only the not-yet-listed targets —
      // already-listed ones stay put rather than being (illegally) re-attached.
      const firstExisting = targets.find((p) => !p.listOrNullObject.isNullObject);
      let list: Word.List;
      let seed: Word.Paragraph | undefined;
      if (firstExisting) {
        list = firstExisting.listOrNullObject;
      } else {
        seed = targets[0];
        list = seed.startNewList();
        list.load("id");
        await context.sync();
      }

      // Count only paragraphs that actually end up in THIS list — a target that
      // belongs to a *different* pre-existing list is left in place (attaching it
      // would throw), so it must not inflate appliedTo. Anything else would be the
      // same silent over-count this PR set out to remove.
      let appliedTo = 0;
      for (const p of targets) {
        if (p === seed) {
          appliedTo++; // seeds the freshly-created list
          continue;
        }
        const membership = p.listOrNullObject;
        if (membership.isNullObject) {
          p.attachToList(list.id, 0);
          appliedTo++;
        } else if (membership.id === list.id) {
          appliedTo++; // already in the list we're building
        }
        // else: belongs to a different list — left as-is, not counted.
      }

      configure(list);
      await context.sync();
      const skipped = targets.length - appliedTo;
      return {
        success: true,
        appliedTo,
        ...(skipped > 0
          ? {
              note:
                `${skipped} target paragraph(s) already belonged to a different list and were left ` +
                `in place. Convert them separately if you want them in this list.`,
            }
          : {}),
      };
    } catch (err) {
      // Office can still reject the list build on a document whose paragraphs
      // span incompatible list structures (e.g. HTML-imported nesting). Return a
      // structured, actionable error instead of an opaque GeneralException.
      const message = (err as { message?: string })?.message ?? String(err);
      return {
        error:
          `Could not build the list: ${message}. Some target paragraphs may belong to ` +
          `incompatible lists — re-read get_paragraphs and try a smaller/cleaner range.`,
      };
    }
  });
}

/** Load the document's comments and return the one with `id`, or null. */
async function findCommentById(
  context: Word.RequestContext,
  id: string
): Promise<Word.Comment | null> {
  const comments = context.document.body.getComments();
  comments.load("items/id");
  await context.sync();
  return comments.items.find((c) => c.id === id) ?? null;
}

/**
 * Shared preamble for the by-id comment mutators (reply / resolve / delete):
 * validate the id, look up the comment, null-guard, run `op`, then sync. `op`'s
 * return value (if any) becomes the tool result; otherwise `{ success: true }`.
 */
async function runWithComment(
  args: Record<string, unknown>,
  op: (comment: Word.Comment, context: Word.RequestContext) => unknown
): Promise<unknown> {
  const { id, error } = validateCommentId(args.commentId);
  if (error) return { error };
  return Word.run(async (context) => {
    const comment = await findCommentById(context, id as string);
    if (!comment) return { error: `No comment found with id "${id}".` };
    const result = op(comment, context);
    await context.sync();
    return result ?? { success: true };
  });
}

/**
 * Shared implementation for `set_header` / `set_footer`. Replaces the first
 * section's header or footer body with HTML (if given) or plain text. Replace
 * semantics clobber any existing header/footer content for that type — this is
 * intentional and called out in the tool descriptions.
 */
async function setHeaderFooter(
  args: Record<string, unknown>,
  kind: "header" | "footer"
): Promise<unknown> {
  const type = resolveHeaderFooterType(args.type);
  return Word.run(async (context) => {
    const section = context.document.sections.getFirst();
    const target = kind === "footer" ? section.getFooter(type) : section.getHeader(type);
    if (typeof args.html === "string" && args.html !== "") {
      target.insertHtml(args.html, Word.InsertLocation.replace);
    } else {
      target.insertText((args.text as string) ?? "", Word.InsertLocation.replace);
    }
    await context.sync();
    return { success: true, type };
  });
}

/**
 * Re-read a table's dimensions after a mutation, within the same live context.
 * A same-`Word.run` re-read is the trustworthy post-mutation signal (a separate
 * `get_tables` call can read one sync stale), so the table tools return these so
 * the AI doesn't need — and can't be misled by — a follow-up read.
 */
async function readTableDims(
  context: Word.RequestContext,
  table: Word.Table
): Promise<{ rowCount: number; columnCount: number }> {
  table.load("rowCount,values");
  await context.sync();
  return { rowCount: table.rowCount, columnCount: tableColumnCount(table.values) };
}

/** Outcome of resolving tracked changes: how many committed + how many remain. */
export interface ResolveTrackedChangesResult {
  processed: number;
  remaining: number;
  /** Set when the loop stopped early (a non-persisting host, or a thrown op). */
  error?: string;
}

/**
 * Accept or reject every tracked change, one op per `context.sync()`.
 *
 * A bulk `TrackedChangeCollection.acceptAll()/rejectAll()` (or a `forEach` over
 * `items`) settles in a single sync and — like bulk `Comment.delete()` — can
 * silently no-op on some hosts (Word for Mac; see solution 51d08f99). So we
 * re-fetch a fresh collection each iteration and resolve its FIRST change,
 * re-reading the count in the SAME sync that commits the op. This persists
 * reliably, dodges the collection re-indexing as items are resolved, and —
 * crucially — lets us verify progress: if an op doesn't shrink the collection
 * (the silent-no-op host this exists to defend against) we stop instead of
 * spinning to the cap and returning a `processed` count that exceeds reality.
 *
 * `processed` therefore counts CONFIRMED resolutions, not loop iterations.
 * Each op commits in its own sync, so a mid-loop throw would otherwise lose the
 * partial count on an irreversible operation; we catch it and report the
 * partial state (`processed`/`remaining`/`error`) truthfully rather than letting
 * the throw reach the AI opaque. Exported for unit testing against a fake context.
 */
export async function acceptOrRejectAllTrackedChanges(
  context: Word.RequestContext,
  action: "accept" | "reject"
): Promise<ResolveTrackedChangesResult> {
  const initial = context.document.body.getTrackedChanges();
  initial.load("items/type");
  await context.sync();
  let remaining = initial.items.length;
  const cap = remaining + 5; // guard: a healthy run resolves one per iteration

  let processed = 0;
  try {
    for (let i = 0; i < cap && remaining > 0; i++) {
      const first = context.document.body.getTrackedChanges().getFirstOrNullObject();
      first.load("isNullObject");
      await context.sync();
      if (first.isNullObject) break;

      if (action === "accept") first.accept();
      else first.reject();

      // Commit the op AND re-read the count in one sync, so we can confirm the
      // collection actually shrank before crediting this as processed.
      const after = context.document.body.getTrackedChanges();
      after.load("items/type");
      await context.sync();
      const newRemaining = after.items.length;

      if (newRemaining >= remaining) {
        // No progress: the op didn't persist on this host (solution 51d08f99).
        // Stop rather than over-count; report it so `success` can be false.
        return {
          processed,
          remaining: newRemaining,
          error: `Stopped: a tracked change did not ${action} on this host (no progress after ${processed}). Resolve remaining revisions in Word's Review pane.`,
        };
      }
      processed++;
      remaining = newRemaining;
    }
  } catch (e) {
    // Each op already committed in its own sync, so `processed` changes are
    // permanent. Report the partial state instead of throwing opaquely.
    const message = e instanceof Error ? e.message : String(e);
    return { processed, remaining, error: `Stopped after ${processed} ${action}(s): ${message}` };
  }

  return { processed, remaining };
}

// ---------------------------------------------------------------------------
// Tool definitions
// ---------------------------------------------------------------------------

const tools: WordTool[] = [
  // ─── Document Info ────────────────────────────────────────────────────────

  {
    schema: {
      name: "get_document_info",
      description:
        "Get high-level information about the current Word document: word count, paragraph count, and character count.",
      parameters: {
        type: "object",
        properties: {},
      },
    },
    handler: async () => {
      return Word.run(async (context) => {
        const body = context.document.body;
        const paragraphs = body.paragraphs;
        body.load("text");
        paragraphs.load("items/text");
        await context.sync();

        const text = body.text;
        const wordCount = text.trim() === "" ? 0 : text.trim().split(/\s+/).length;
        const charCount = text.length;
        const paraCount = paragraphs.items.length;

        return { wordCount, characterCount: charCount, paragraphCount: paraCount };
      });
    },
  },

  // ─── Read ─────────────────────────────────────────────────────────────────

  {
    schema: {
      name: "get_document_text",
      description: "Get the full plain text content of the Word document.",
      parameters: {
        type: "object",
        properties: {},
      },
    },
    handler: async () => {
      return Word.run(async (context) => {
        const body = context.document.body;
        body.load("text");
        await context.sync();
        return { text: body.text };
      });
    },
  },

  {
    schema: {
      name: "get_selected_text",
      description: "Get the text that is currently selected by the user in the document.",
      parameters: {
        type: "object",
        properties: {},
      },
    },
    handler: async () => {
      return Word.run(async (context) => {
        const selection = context.document.getSelection();
        selection.load("text");
        await context.sync();
        return { text: selection.text };
      });
    },
  },

  {
    schema: {
      name: "get_paragraphs",
      description: "Get all paragraphs in the document with their text, style, and alignment.",
      parameters: {
        type: "object",
        properties: {},
      },
    },
    handler: async () => {
      return Word.run(async (context) => {
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load("items/text,items/style,items/alignment");
        await context.sync();

        return {
          paragraphs: paragraphs.items.map((p, i) => ({
            index: i,
            text: p.text,
            style: p.style,
            alignment: p.alignment,
          })),
        };
      });
    },
  },

  {
    schema: {
      name: "get_paragraph_count",
      description: "Get the total number of paragraphs in the document.",
      parameters: {
        type: "object",
        properties: {},
      },
    },
    handler: async () => {
      return Word.run(async (context) => {
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load("items/text");
        await context.sync();
        return { count: paragraphs.items.length };
      });
    },
  },

  // ─── Write ────────────────────────────────────────────────────────────────

  {
    schema: {
      name: "insert_text",
      description:
        "Insert text at the current selection. insertLocation controls where relative to the selection: 'Replace' (default), 'Start', 'End', 'Before', or 'After'.",
      parameters: {
        type: "object",
        properties: {
          text: {
            type: "string",
            description: "The text to insert.",
          },
          insertLocation: {
            type: "string",
            enum: ["Replace", "Start", "End", "Before", "After"],
            description:
              "Where to insert relative to the selection. 'Start'/'End' insert inside the selection's range; 'Before'/'After' insert outside it. Defaults to 'Replace'.",
          },
        },
        required: ["text"],
      },
    },
    handler: async (args) => {
      return Word.run(async (context) => {
        const selection = context.document.getSelection();
        const location = (args.insertLocation as Word.InsertLocation | "Replace" | "Start" | "End" | "Before" | "After") ?? Word.InsertLocation.replace;
        selection.insertText(args.text as string, location);
        await context.sync();
        return { success: true };
      });
    },
  },

  {
    schema: {
      name: "insert_paragraph",
      description: "Insert a new paragraph with the given text at the start or end of the document body.",
      parameters: {
        type: "object",
        properties: {
          text: {
            type: "string",
            description: "Text for the new paragraph.",
          },
          insertLocation: {
            type: "string",
            enum: ["Start", "End"],
            description: "Where in the body to insert. Defaults to 'End'.",
          },
        },
        required: ["text"],
      },
    },
    handler: async (args) => {
      return Word.run(async (context) => {
        const body = context.document.body;
        const location = (args.insertLocation as Word.InsertLocation.start | Word.InsertLocation.end | "Start" | "End") ?? Word.InsertLocation.end;
        body.insertParagraph(args.text as string, location);
        await context.sync();
        return { success: true };
      });
    },
  },

  {
    schema: {
      name: "replace_text",
      description:
        "Find all occurrences of a string in the document and replace them with new text. Supports Word search options including wildcard (regex-like) matching.",
      parameters: {
        type: "object",
        properties: {
          find: {
            type: "string",
            description: "The text (or wildcard pattern, when matchWildcards is true) to search for.",
          },
          replace: {
            type: "string",
            description: "The replacement text.",
          },
          matchCase: { type: "boolean", description: "Match case. Defaults to false." },
          matchWholeWord: { type: "boolean", description: "Match whole words only. Defaults to false." },
          matchWildcards: {
            type: "boolean",
            description: "Treat 'find' as a Word wildcard (regex-like) pattern. Defaults to false.",
          },
          matchPrefix: { type: "boolean", description: "Match only at the start of a word. Defaults to false." },
          matchSuffix: { type: "boolean", description: "Match only at the end of a word. Defaults to false." },
          ignorePunct: { type: "boolean", description: "Ignore punctuation between characters. Defaults to false." },
          ignoreSpace: { type: "boolean", description: "Ignore whitespace between characters. Defaults to false." },
        },
        required: ["find", "replace"],
      },
    },
    handler: async (args) => {
      return Word.run(async (context) => {
        const results = context.document.body.search(args.find as string, buildSearchOptions(args));
        results.load("items/text");
        await context.sync();

        const count = results.items.length;
        results.items.forEach((range) => {
          range.insertText(args.replace as string, Word.InsertLocation.replace);
        });
        await context.sync();

        return { success: true, replacedCount: count };
      });
    },
  },

  // ─── Formatting ───────────────────────────────────────────────────────────

  {
    schema: {
      name: "format_text",
      description:
        "Apply font formatting (bold, italic, underline, size, color, highlight, strikethrough, sub/superscript, caps, hidden) to the selection, or to specific paragraphs via paragraphIndices / match. Some effects need a desktop Word host and are reported back as unsupported when unavailable.",
      parameters: {
        type: "object",
        properties: {
          bold: { type: "boolean", description: "Set bold." },
          italic: { type: "boolean", description: "Set italic." },
          underline: {
            type: "string",
            description: "Underline style, e.g. 'Single', 'None'. Set to 'None' to remove.",
          },
          size: { type: "number", description: "Font size in points." },
          color: { type: "string", description: "Font color as hex string, e.g. '#FF0000'." },
          highlightColor: { type: "string", description: "Highlight color as hex string, e.g. '#FFFF00'." },
          strikeThrough: { type: "boolean", description: "Single strikethrough." },
          doubleStrikeThrough: { type: "boolean", description: "Double strikethrough." },
          subscript: { type: "boolean", description: "Subscript." },
          superscript: { type: "boolean", description: "Superscript." },
          allCaps: { type: "boolean", description: "All caps (desktop Word only)." },
          smallCaps: { type: "boolean", description: "Small caps (desktop Word only)." },
          hidden: { type: "boolean", description: "Hidden text (desktop Word only)." },
          paragraphIndices: {
            type: "array",
            items: { type: "number" },
            description:
              "0-based paragraph indices (from get_paragraphs) to format. When omitted with no 'match', formats the current selection.",
          },
          match: {
            type: "string",
            description: "Format every paragraph whose text contains this string. Combines with paragraphIndices.",
          },
        },
      },
    },
    handler: async (args) => applyFontTool(args),
  },

  {
    schema: {
      name: "apply_style",
      description:
        "Apply a built-in Word paragraph style (e.g. 'Heading 1', 'Heading 2', 'Normal', 'Title', 'Subtitle', 'Quote') to paragraphs. Target specific paragraphs with paragraphIndices and/or match; with neither, styles the current selection. Returns the actual number of paragraphs changed (appliedTo) — use it to confirm bulk changes rather than assuming success.",
      parameters: {
        type: "object",
        properties: {
          styleName: {
            type: "string",
            description: "Name of the Word built-in style to apply.",
          },
          paragraphIndices: {
            type: "array",
            items: { type: "number" },
            description:
              "0-based paragraph indices (from get_paragraphs) to style. When omitted with no 'match', styles the current selection.",
          },
          match: {
            type: "string",
            description:
              "Style every paragraph whose text contains this string (e.g. style all section headings at once). Combines with paragraphIndices.",
          },
          matchMode: {
            type: "string",
            enum: ["contains", "exact"],
            description:
              "How 'match' compares: 'contains' (default, substring) or 'exact' (whole paragraph, case-insensitive). Use 'exact' for short titles that also appear inside body text, to avoid styling those paragraphs too.",
          },
        },
        required: ["styleName"],
      },
    },
    handler: async (args) => {
      return Word.run(async (context) => {
        const targets = await resolveTargetParagraphs(context, args);
        targets.forEach((p) => {
          p.style = args.styleName as string;
        });
        await context.sync();
        return { success: true, appliedTo: targets.length, ...staleTargetNote(args, targets.length) };
      });
    },
  },

  {
    schema: {
      name: "set_font",
      description:
        "Set multiple font properties at once on the selection, or on specific paragraphs via paragraphIndices / match. Some effects (all-caps, small-caps, hidden) need a desktop Word host and are reported back as unsupported when unavailable.",
      parameters: {
        type: "object",
        properties: {
          name: { type: "string", description: "Font family name, e.g. 'Calibri'." },
          size: { type: "number", description: "Font size in points." },
          color: { type: "string", description: "Font color as hex, e.g. '#000000'." },
          bold: { type: "boolean" },
          italic: { type: "boolean" },
          underline: { type: "string", description: "Underline style, e.g. 'Single', 'None'." },
          highlightColor: { type: "string", description: "Highlight color as hex, e.g. '#FFFF00'." },
          strikeThrough: { type: "boolean", description: "Single strikethrough." },
          doubleStrikeThrough: { type: "boolean", description: "Double strikethrough." },
          subscript: { type: "boolean", description: "Subscript." },
          superscript: { type: "boolean", description: "Superscript." },
          allCaps: { type: "boolean", description: "All caps (desktop Word only)." },
          smallCaps: { type: "boolean", description: "Small caps (desktop Word only)." },
          hidden: { type: "boolean", description: "Hidden text (desktop Word only)." },
          paragraphIndices: {
            type: "array",
            items: { type: "number" },
            description:
              "0-based paragraph indices (from get_paragraphs) to format. When omitted with no 'match', formats the current selection.",
          },
          match: {
            type: "string",
            description: "Format every paragraph whose text contains this string. Combines with paragraphIndices.",
          },
        },
      },
    },
    handler: async (args) => applyFontTool(args),
  },

  {
    schema: {
      name: "set_paragraph_format",
      description:
        "Set paragraph layout — alignment, line spacing, space before/after, indents, and keep-with-next / keep-together — on the selection or on specific paragraphs via paragraphIndices / match. keepWithNext/keepTogether need a desktop Word host and are reported back as unsupported when unavailable.",
      parameters: {
        type: "object",
        properties: {
          alignment: {
            type: "string",
            enum: ["left", "centered", "right", "justified"],
            description: "Paragraph alignment.",
          },
          lineSpacing: { type: "number", description: "Line spacing in points (e.g. 18 ≈ 1.5× for 12pt text)." },
          spaceBefore: { type: "number", description: "Space before the paragraph, in points." },
          spaceAfter: { type: "number", description: "Space after the paragraph, in points." },
          leftIndent: { type: "number", description: "Left indent, in points." },
          rightIndent: { type: "number", description: "Right indent, in points." },
          firstLineIndent: { type: "number", description: "First-line indent, in points (negative = hanging)." },
          keepWithNext: { type: "boolean", description: "Keep paragraph with the next one (desktop Word only)." },
          keepTogether: { type: "boolean", description: "Keep all lines of the paragraph together (desktop Word only)." },
          paragraphIndices: {
            type: "array",
            items: { type: "number" },
            description:
              "0-based paragraph indices (from get_paragraphs) to format. When omitted with no 'match', formats the current selection.",
          },
          match: {
            type: "string",
            description: "Format every paragraph whose text contains this string. Combines with paragraphIndices.",
          },
        },
      },
    },
    handler: async (args) => applyParagraphFormatTool(args),
  },

  {
    // Back-compat alias for set_paragraph_format (alignment only). Kept so existing
    // callers / prompts that emit set_paragraph_alignment keep working.
    schema: {
      name: "set_paragraph_alignment",
      description:
        "Set the alignment of paragraphs ('left', 'centered', 'right', 'justified'). Alias of set_paragraph_format; targets the selection, or paragraphIndices / match when given.",
      parameters: {
        type: "object",
        properties: {
          alignment: {
            type: "string",
            enum: ["left", "centered", "right", "justified"],
            description: "The paragraph alignment.",
          },
          paragraphIndices: {
            type: "array",
            items: { type: "number" },
            description: "0-based paragraph indices to align. When omitted with no 'match', aligns the selection.",
          },
          match: {
            type: "string",
            description: "Align every paragraph whose text contains this string. Combines with paragraphIndices.",
          },
        },
        required: ["alignment"],
      },
    },
    handler: async (args) => applyParagraphFormatTool(args),
  },

  // ─── Search ───────────────────────────────────────────────────────────────

  {
    schema: {
      name: "search_text",
      description:
        "Search for text in the document and return all matching ranges with their text and font info. Supports Word search options including wildcard (regex-like) matching.",
      parameters: {
        type: "object",
        properties: {
          query: {
            type: "string",
            description: "The text (or wildcard pattern, when matchWildcards is true) to search for.",
          },
          matchCase: { type: "boolean", description: "Match case. Defaults to false." },
          matchWholeWord: { type: "boolean", description: "Match whole words only. Defaults to false." },
          matchWildcards: {
            type: "boolean",
            description: "Treat 'query' as a Word wildcard (regex-like) pattern. Defaults to false.",
          },
          matchPrefix: { type: "boolean", description: "Match only at the start of a word. Defaults to false." },
          matchSuffix: { type: "boolean", description: "Match only at the end of a word. Defaults to false." },
          ignorePunct: { type: "boolean", description: "Ignore punctuation between characters. Defaults to false." },
          ignoreSpace: { type: "boolean", description: "Ignore whitespace between characters. Defaults to false." },
        },
        required: ["query"],
      },
    },
    handler: async (args) => {
      return Word.run(async (context) => {
        const results = context.document.body.search(args.query as string, buildSearchOptions(args));
        results.load("items/text,items/font");
        await context.sync();

        return {
          count: results.items.length,
          matches: results.items.map((r) => ({
            text: r.text,
            bold: r.font.bold,
            italic: r.font.italic,
            color: r.font.color,
          })),
        };
      });
    },
  },

  // ─── Tables ───────────────────────────────────────────────────────────────

  {
    schema: {
      name: "insert_table",
      description: "Insert a table at the current selection. Provide row/column counts and optional initial data.",
      parameters: {
        type: "object",
        properties: {
          rows: {
            type: "number",
            description: "Number of rows.",
          },
          columns: {
            type: "number",
            description: "Number of columns.",
          },
          insertLocation: {
            type: "string",
            enum: ["Before", "After"],
            description: "Where to insert relative to the selection. Defaults to 'After'.",
          },
          data: {
            type: "array",
            description: "2-D array of strings for initial cell values (rows × cols). Optional.",
          },
        },
        required: ["rows", "columns"],
      },
    },
    handler: async (args) => {
      return Word.run(async (context) => {
        const location = (args.insertLocation as Word.InsertLocation.before | Word.InsertLocation.after | "Before" | "After") ?? Word.InsertLocation.after;
        const data = (args.data as string[][] | undefined) ?? [];
        const selection = context.document.getSelection();
        selection.insertTable(args.rows as number, args.columns as number, location, data);
        await context.sync();
        return { success: true };
      });
    },
  },

  {
    schema: {
      name: "get_tables",
      description: "Get all tables in the document with their cell values.",
      parameters: {
        type: "object",
        properties: {},
      },
    },
    handler: async () => {
      return Word.run(async (context) => {
        const tables = context.document.body.tables;
        tables.load("items/values,items/rowCount");
        await context.sync();

        return {
          count: tables.items.length,
          tables: tables.items.map((t, i) => ({
            index: i,
            rowCount: t.rowCount,
            columnCount: tableColumnCount(t.values),
            values: t.values,
          })),
        };
      });
    },
  },

  {
    schema: {
      name: "set_table_cell",
      description: "Set the text content of a specific table cell (0-based row and column indices).",
      parameters: {
        type: "object",
        properties: {
          tableIndex: {
            type: "number",
            description: "0-based index of the table in the document.",
          },
          row: {
            type: "number",
            description: "0-based row index.",
          },
          column: {
            type: "number",
            description: "0-based column index.",
          },
          text: {
            type: "string",
            description: "Text to set in the cell.",
          },
        },
        required: ["tableIndex", "row", "column", "text"],
      },
    },
    handler: async (args) => {
      return Word.run(async (context) => {
        const tables = context.document.body.tables;
        tables.load("items/rowCount");
        await context.sync();

        const table = tables.items[args.tableIndex as number];
        // Return a structured error (like every sibling table tool) rather than
        // throwing: a stale tableIndex after other edits is an ordinary, model-
        // recoverable condition, not an exception to surface as a failed call.
        if (!table) return { error: `Table at index ${args.tableIndex} not found.` };

        const cell = table.getCell(args.row as number, args.column as number);
        cell.body.insertText(args.text as string, Word.InsertLocation.replace);
        await context.sync();
        return { success: true };
      });
    },
  },

  {
    minRequirementSet: "1.3", // Table.addRows
    schema: {
      name: "add_table_rows",
      description:
        "Add one or more rows to a table (0-based tableIndex). New rows copy the first/last existing row as a template and are added at the 'End' (default) or 'Start'. Optional 'data' fills the new cells.",
      parameters: {
        type: "object",
        properties: {
          tableIndex: { type: "number", description: "0-based index of the table in the document." },
          count: { type: "number", description: "Number of rows to add (positive integer)." },
          insertLocation: {
            type: "string",
            enum: ["Start", "End"],
            description: "Add at the table 'Start' or 'End'. Defaults to 'End'.",
          },
          data: {
            type: "array",
            description: "Optional 2-D array of strings for the new rows' cell values.",
          },
        },
        required: ["tableIndex", "count"],
      },
    },
    handler: async (args) => {
      const { count, error } = validatePositiveCount(args.count);
      if (error) return { error };
      const edge = resolveTableEdge(args.insertLocation);
      return Word.run(async (context) => {
        const tables = context.document.body.tables;
        tables.load("items/rowCount");
        await context.sync();

        const table = tables.items[args.tableIndex as number];
        if (!table) return { error: `Table at index ${args.tableIndex} not found.` };

        table.addRows(edge, count as number, args.data as string[][] | undefined);
        await context.sync();
        return { success: true, added: count, ...(await readTableDims(context, table)) };
      });
    },
  },

  {
    minRequirementSet: "1.3", // Table.addColumns
    schema: {
      name: "add_table_columns",
      description:
        "Add one or more columns to a table (0-based tableIndex). New columns copy the first/last existing column as a template and are added at the 'End' (default) or 'Start'. Applies to uniform tables. Optional 'data' fills the new cells.",
      parameters: {
        type: "object",
        properties: {
          tableIndex: { type: "number", description: "0-based index of the table in the document." },
          count: { type: "number", description: "Number of columns to add (positive integer)." },
          insertLocation: {
            type: "string",
            enum: ["Start", "End"],
            description: "Add at the table 'Start' or 'End'. Defaults to 'End'.",
          },
          data: {
            type: "array",
            description: "Optional 2-D array of strings for the new columns' cell values.",
          },
        },
        required: ["tableIndex", "count"],
      },
    },
    handler: async (args) => {
      const { count, error } = validatePositiveCount(args.count);
      if (error) return { error };
      const edge = resolveTableEdge(args.insertLocation);
      return Word.run(async (context) => {
        const tables = context.document.body.tables;
        tables.load("items/rowCount");
        await context.sync();

        const table = tables.items[args.tableIndex as number];
        if (!table) return { error: `Table at index ${args.tableIndex} not found.` };

        table.addColumns(edge, count as number, args.data as string[][] | undefined);
        await context.sync();
        return { success: true, added: count, ...(await readTableDims(context, table)) };
      });
    },
  },

  {
    minRequirementSet: "1.3", // TableCell.deleteRow
    schema: {
      name: "delete_table_row",
      description: "Delete a single row from a table by 0-based row index.",
      parameters: {
        type: "object",
        properties: {
          tableIndex: { type: "number", description: "0-based index of the table in the document." },
          row: { type: "number", description: "0-based index of the row to delete." },
        },
        required: ["tableIndex", "row"],
      },
    },
    handler: async (args) => {
      const rowCheck = validateTableIndex(args.row, "row");
      if (rowCheck.error) return { error: rowCheck.error };
      return Word.run(async (context) => {
        const tables = context.document.body.tables;
        tables.load("items/rowCount");
        await context.sync();

        const table = tables.items[args.tableIndex as number];
        if (!table) return { error: `Table at index ${args.tableIndex} not found.` };
        if ((rowCheck.index as number) >= table.rowCount) {
          return { error: `Row ${rowCheck.index} is out of range (table has ${table.rowCount} rows).` };
        }

        table.getCell(rowCheck.index as number, 0).deleteRow();
        await context.sync();
        return { success: true, ...(await readTableDims(context, table)) };
      });
    },
  },

  {
    minRequirementSet: "1.3", // TableCell.deleteColumn (portable; avoids desktop-only TableColumn)
    schema: {
      name: "delete_table_column",
      description: "Delete a single column from a table by 0-based column index. Applies to uniform tables.",
      parameters: {
        type: "object",
        properties: {
          tableIndex: { type: "number", description: "0-based index of the table in the document." },
          column: { type: "number", description: "0-based index of the column to delete." },
        },
        required: ["tableIndex", "column"],
      },
    },
    handler: async (args) => {
      const colCheck = validateTableIndex(args.column, "column");
      if (colCheck.error) return { error: colCheck.error };
      return Word.run(async (context) => {
        const tables = context.document.body.tables;
        tables.load("items/values");
        await context.sync();

        const table = tables.items[args.tableIndex as number];
        if (!table) return { error: `Table at index ${args.tableIndex} not found.` };
        const columnCount = tableColumnCount(table.values);
        if ((colCheck.index as number) >= columnCount) {
          return { error: `Column ${colCheck.index} is out of range (table has ${columnCount} columns).` };
        }

        table.getCell(0, colCheck.index as number).deleteColumn();
        await context.sync();
        return { success: true, ...(await readTableDims(context, table)) };
      });
    },
  },

  {
    minRequirementSet: "1.4", // Table.mergeCells
    schema: {
      name: "merge_table_cells",
      description:
        "Merge a rectangular block of table cells bounded inclusively by a top-left (topRow, firstColumn) and bottom-right (bottomRow, lastColumn) cell. All indices are 0-based.",
      parameters: {
        type: "object",
        properties: {
          tableIndex: { type: "number", description: "0-based index of the table in the document." },
          topRow: { type: "number", description: "0-based row of the top-left cell." },
          firstColumn: { type: "number", description: "0-based column of the top-left cell." },
          bottomRow: { type: "number", description: "0-based row of the bottom-right cell." },
          lastColumn: { type: "number", description: "0-based column of the bottom-right cell." },
        },
        required: ["tableIndex", "topRow", "firstColumn", "bottomRow", "lastColumn"],
      },
    },
    handler: async (args) => {
      for (const key of ["topRow", "firstColumn", "bottomRow", "lastColumn"] as const) {
        const check = validateTableIndex(args[key], key);
        if (check.error) return { error: check.error };
      }
      if (
        (args.bottomRow as number) < (args.topRow as number) ||
        (args.lastColumn as number) < (args.firstColumn as number)
      ) {
        return { error: "bottomRow/lastColumn must be greater than or equal to topRow/firstColumn." };
      }
      return Word.run(async (context) => {
        const tables = context.document.body.tables;
        tables.load("items/rowCount,items/values");
        await context.sync();

        const table = tables.items[args.tableIndex as number];
        if (!table) return { error: `Table at index ${args.tableIndex} not found.` };
        const columnCount = tableColumnCount(table.values);
        if ((args.bottomRow as number) >= table.rowCount || (args.lastColumn as number) >= columnCount) {
          return {
            error: `Merge range is out of bounds (table has ${table.rowCount} rows, ${columnCount} columns).`,
          };
        }

        table.mergeCells(
          args.topRow as number,
          args.firstColumn as number,
          args.bottomRow as number,
          args.lastColumn as number
        );
        await context.sync();
        return { success: true, ...(await readTableDims(context, table)) };
      });
    },
  },

  {
    minRequirementSet: "1.3", // Table.styleBuiltIn + style-option flags
    schema: {
      name: "set_table_style",
      description:
        "Apply a built-in table style and/or toggle style options on a table (0-based tableIndex). 'styleName' is a built-in name like 'GridTable4_Accent1'. The booleans toggle the header row, banded rows/columns, first/last column, and total row. Provide at least one.",
      parameters: {
        type: "object",
        properties: {
          tableIndex: { type: "number", description: "0-based index of the table in the document." },
          styleName: {
            type: "string",
            description: "Built-in table style name, e.g. 'GridTable4_Accent1', 'ListTable3_Accent2', 'PlainTable1', 'TableGrid'.",
          },
          headerRow: { type: "boolean", description: "Whether the first row is a special header row." },
          bandedRows: { type: "boolean", description: "Whether to band alternate rows." },
          bandedColumns: { type: "boolean", description: "Whether to band alternate columns." },
          firstColumn: { type: "boolean", description: "Whether the first column has special styling." },
          lastColumn: { type: "boolean", description: "Whether the last column has special styling." },
          totalRow: { type: "boolean", description: "Whether the last row is a special total row." },
        },
        required: ["tableIndex"],
      },
    },
    handler: async (args) => {
      const assignments = collectTableStyleAssignments(args);
      if (Object.keys(assignments).length === 0) {
        return {
          error:
            "Provide at least one of: styleName, headerRow, bandedRows, bandedColumns, firstColumn, lastColumn, totalRow.",
        };
      }
      return Word.run(async (context) => {
        const tables = context.document.body.tables;
        tables.load("items/rowCount");
        await context.sync();

        const table = tables.items[args.tableIndex as number];
        if (!table) return { error: `Table at index ${args.tableIndex} not found.` };

        if (assignments.styleBuiltIn !== undefined) {
          table.styleBuiltIn = assignments.styleBuiltIn as unknown as Word.BuiltInStyleName;
        }
        if (assignments.headerRowCount !== undefined) table.headerRowCount = assignments.headerRowCount;
        if (assignments.styleBandedRows !== undefined) table.styleBandedRows = assignments.styleBandedRows;
        if (assignments.styleBandedColumns !== undefined) table.styleBandedColumns = assignments.styleBandedColumns;
        if (assignments.styleFirstColumn !== undefined) table.styleFirstColumn = assignments.styleFirstColumn;
        if (assignments.styleLastColumn !== undefined) table.styleLastColumn = assignments.styleLastColumn;
        if (assignments.styleTotalRow !== undefined) table.styleTotalRow = assignments.styleTotalRow;
        await context.sync();
        return { success: true, ...(await readTableDims(context, table)) };
      });
    },
  },

  // ─── Structure ────────────────────────────────────────────────────────────

  {
    schema: {
      name: "insert_page_break",
      description: "Insert a page break after the current selection.",
      parameters: {
        type: "object",
        properties: {},
      },
    },
    handler: async () => {
      return Word.run(async (context) => {
        const selection = context.document.getSelection();
        selection.insertBreak(Word.BreakType.page, Word.InsertLocation.after);
        await context.sync();
        return { success: true };
      });
    },
  },

  {
    schema: {
      name: "clear_document",
      description: "Clear all content from the document body. This is permanent.",
      parameters: {
        type: "object",
        properties: {},
      },
    },
    handler: async () => {
      return Word.run(async (context) => {
        context.document.body.clear();
        await context.sync();
        return { success: true };
      });
    },
  },

  {
    schema: {
      name: "insert_html",
      description:
        "Insert HTML content relative to the current selection: 'Replace' (default), 'Start', 'End', 'Before', or 'After'.",
      parameters: {
        type: "object",
        properties: {
          html: {
            type: "string",
            description: "HTML string to insert.",
          },
          insertLocation: {
            type: "string",
            enum: ["Replace", "Start", "End", "Before", "After"],
            description:
              "Where to insert relative to the selection. 'Start'/'End' insert inside the selection's range; 'Before'/'After' insert outside it. Defaults to 'Replace'.",
          },
        },
        required: ["html"],
      },
    },
    handler: async (args) => {
      return Word.run(async (context) => {
        const selection = context.document.getSelection();
        const location = (args.insertLocation as Word.InsertLocation | "Replace" | "Start" | "End" | "Before" | "After") ?? Word.InsertLocation.replace;
        selection.insertHtml(args.html as string, location);
        await context.sync();
        return { success: true };
      });
    },
  },

  {
    // `Range.insertComment` is WordApi 1.4 — gate so hosts without it never see
    // (or crash on) this tool. Every modern Word host supports 1.4, so this is a
    // no-op in practice; it also serves as the reference gated tool for WT1+.
    minRequirementSet: "1.4",
    schema: {
      name: "add_comment",
      description: "Add a comment to the current selection.",
      parameters: {
        type: "object",
        properties: {
          text: {
            type: "string",
            description: "The comment text.",
          },
        },
        required: ["text"],
      },
    },
    handler: async (args) => {
      return Word.run(async (context) => {
        const selection = context.document.getSelection();
        selection.insertComment(args.text as string);
        await context.sync();
        return { success: true };
      });
    },
  },

  // ─── Rich media & links (WT2) ─────────────────────────────────────────────

  {
    minRequirementSet: "1.2", // Range.insertInlinePictureFromBase64
    schema: {
      name: "insert_image",
      description:
        "Insert an inline image at the selection from base64-encoded image data (PNG/JPG/GIF). The base64 may include or omit a data-URL prefix (e.g. 'data:image/png;base64,'). insertLocation: 'Replace' (default), 'Start', 'End', 'Before', 'After'.",
      parameters: {
        type: "object",
        properties: {
          base64: {
            type: "string",
            description: "Base64-encoded image data, with or without a data-URL prefix.",
          },
          insertLocation: {
            type: "string",
            enum: ["Replace", "Start", "End", "Before", "After"],
            description: "Where to insert relative to the selection. Defaults to 'Replace'.",
          },
        },
        required: ["base64"],
      },
    },
    handler: async (args) => {
      const { base64, error } = normalizeImageBase64(args.base64);
      if (error) return { error };
      return Word.run(async (context) => {
        const selection = context.document.getSelection();
        const location = (args.insertLocation as Word.InsertLocation | "Replace" | "Start" | "End" | "Before" | "After") ?? Word.InsertLocation.replace;
        selection.insertInlinePictureFromBase64(base64 as string, location);
        await context.sync();
        return { success: true };
      });
    },
  },

  {
    minRequirementSet: "1.4", // Range.hyperlink (settable)
    schema: {
      name: "insert_hyperlink",
      description:
        "Create a hyperlink. With 'displayText', inserts that linked text at the selection; otherwise links the current selection, or the first occurrence of 'find' when given. 'address' is the target URL.",
      parameters: {
        type: "object",
        properties: {
          address: {
            type: "string",
            description: "Link target: http(s)://…, mailto:…, #bookmark, or /path.",
          },
          displayText: {
            type: "string",
            description: "Optional visible text for the link. If omitted, the existing selection/match text is linked.",
          },
          find: {
            type: "string",
            description: "Optional: link the first occurrence of this text instead of the current selection.",
          },
        },
        required: ["address"],
      },
    },
    handler: async (args) => {
      const { url, error } = normalizeHyperlinkUrl(args.address);
      if (error) return { error };
      return Word.run(async (context) => {
        let range: Word.Range;
        if (typeof args.find === "string" && args.find !== "") {
          const results = context.document.body.search(args.find, {});
          results.load("items");
          await context.sync();
          if (results.items.length === 0) {
            return { error: `No match found for "${args.find}".` };
          }
          range = results.items[0];
        } else {
          range = context.document.getSelection();
        }
        if (typeof args.displayText === "string" && args.displayText !== "") {
          range = range.insertText(args.displayText, Word.InsertLocation.replace);
        }
        range.hyperlink = url as string;
        await context.sync();
        return { success: true };
      });
    },
  },

  // ─── Lists (WT3) ──────────────────────────────────────────────────────────

  {
    minRequirementSet: "1.3", // startNewList / attachToList / setLevelBullet
    schema: {
      name: "apply_bullets",
      description:
        "Turn paragraphs into a single bulleted list. Target them with paragraphIndices and/or match (recommended for multiple paragraphs); with neither, uses the current selection. Optional 'bullet' style: solid (default), hollow, square, diamond, arrow, checkmark.",
      parameters: {
        type: "object",
        properties: {
          bullet: {
            type: "string",
            description: "Bullet style: solid | hollow | square | diamond | arrow | checkmark. Defaults to solid.",
          },
          paragraphIndices: {
            type: "array",
            items: { type: "number" },
            description:
              "0-based paragraph indices (from get_paragraphs) to list together. When omitted with no 'match', uses the current selection.",
          },
          match: {
            type: "string",
            description: "List every paragraph whose text contains this string. Combines with paragraphIndices.",
          },
        },
      },
    },
    handler: async (args) => {
      const bullet = resolveListBullet(args.bullet);
      return applyListToTargets(args, (list) => list.setLevelBullet(0, bullet));
    },
  },

  {
    minRequirementSet: "1.3", // startNewList / attachToList / setLevelNumbering
    schema: {
      name: "apply_numbering",
      description:
        "Turn paragraphs into a single numbered list (1,2,3…). Target them with paragraphIndices and/or match (recommended for multiple paragraphs); with neither, uses the current selection. Optional 'numbering' style: arabic (default), upperRoman, lowerRoman, upperLetter, lowerLetter.",
      parameters: {
        type: "object",
        properties: {
          numbering: {
            type: "string",
            description:
              "Numbering style: arabic | upperRoman | lowerRoman | upperLetter | lowerLetter. Defaults to arabic.",
          },
          paragraphIndices: {
            type: "array",
            items: { type: "number" },
            description:
              "0-based paragraph indices (from get_paragraphs) to number together as one list. When omitted with no 'match', uses the current selection.",
          },
          match: {
            type: "string",
            description: "Number every paragraph whose text contains this string. Combines with paragraphIndices.",
          },
        },
      },
    },
    handler: async (args) => {
      const numbering = resolveListNumbering(args.numbering);
      return applyListToTargets(args, (list) => list.setLevelNumbering(0, numbering));
    },
  },

  {
    minRequirementSet: "1.3", // ListItem.level
    schema: {
      name: "set_list_level",
      description:
        "Set the indent level (0–8) of list items. Target them with paragraphIndices and/or match (e.g. the item to nest); with neither, uses the current selection. Only paragraphs already in a list are changed. Use after apply_bullets / apply_numbering to nest items.",
      parameters: {
        type: "object",
        properties: {
          level: {
            type: "number",
            description: "0-based list level: 0 = top level, 1 = first indent, up to 8.",
          },
          paragraphIndices: {
            type: "array",
            items: { type: "number" },
            description:
              "0-based paragraph indices (from get_paragraphs) to re-level. When omitted with no 'match', uses the current selection.",
          },
          match: {
            type: "string",
            description: "Re-level every list paragraph whose text contains this string. Combines with paragraphIndices.",
          },
        },
        required: ["level"],
      },
    },
    handler: async (args) => {
      const { level, error } = resolveListLevel(args.level);
      if (error) return { error };
      return Word.run(async (context) => {
        const targets = await resolveTargetParagraphs(context, args);

        // Detect list membership via `listOrNullObject` (more reliable than
        // `listItemOrNullObject` right after list creation), then set the level
        // through `listItem` on the paragraphs that are in a list.
        targets.forEach((p) => p.listOrNullObject.load("isNullObject"));
        await context.sync();

        let appliedTo = 0;
        targets.forEach((p) => {
          if (!p.listOrNullObject.isNullObject) {
            p.listItem.level = level as number;
            appliedTo++;
          }
        });
        await context.sync();
        // When targets were found but none were list items, the note is more
        // specific than the generic stale-index one — say so.
        const note =
          appliedTo === 0 && targets.length > 0
            ? {
                note:
                  "Matched paragraphs, but none are list items — apply_bullets / apply_numbering them first, " +
                  "or the indices are stale (re-read get_paragraphs).",
              }
            : staleTargetNote(args, appliedTo);
        return { success: true, appliedTo, ...note };
      });
    },
  },

  // ─── Comments & collaboration (WT5) ───────────────────────────────────────

  {
    minRequirementSet: "1.4", // Body.getComments
    schema: {
      name: "get_comments",
      description:
        "List all comments in the document: id, author, text, resolved state, and replies. Use the returned id with reply_to_comment / resolve_comment / delete_comment.",
      parameters: { type: "object", properties: {} },
    },
    handler: async () => {
      return Word.run(async (context) => {
        const comments = context.document.body.getComments();
        comments.load("items/id,items/authorName,items/content,items/resolved,items/creationDate");
        await context.sync();

        comments.items.forEach((c) => c.replies.load("items/authorName,items/content"));
        await context.sync();

        return {
          count: comments.items.length,
          comments: comments.items.map((c) => mapComment(c as unknown as RawComment)),
        };
      });
    },
  },

  {
    minRequirementSet: "1.4", // Comment.reply
    schema: {
      name: "reply_to_comment",
      description: "Add a reply to an existing comment thread, identified by its id (from get_comments).",
      parameters: {
        type: "object",
        properties: {
          commentId: { type: "string", description: "The id of the comment to reply to." },
          text: { type: "string", description: "The reply text." },
        },
        required: ["commentId", "text"],
      },
    },
    handler: async (args) =>
      runWithComment(args, (comment) => {
        comment.reply(args.text as string);
      }),
  },

  {
    minRequirementSet: "1.4", // Comment.resolved
    schema: {
      name: "resolve_comment",
      description: "Mark a comment resolved (or reopen it), identified by its id (from get_comments).",
      parameters: {
        type: "object",
        properties: {
          commentId: { type: "string", description: "The id of the comment." },
          resolved: {
            type: "boolean",
            description: "true to resolve (default), false to reopen.",
          },
        },
        required: ["commentId"],
      },
    },
    handler: async (args) => {
      const resolved = args.resolved === undefined ? true : (args.resolved as boolean);
      return runWithComment(args, (comment) => {
        comment.resolved = resolved;
        return { success: true, resolved };
      });
    },
  },

  {
    minRequirementSet: "1.4", // Comment.delete
    schema: {
      name: "delete_comment",
      description: "Delete a single comment (and its replies), identified by its id (from get_comments).",
      parameters: {
        type: "object",
        properties: {
          commentId: { type: "string", description: "The id of the comment to delete." },
        },
        required: ["commentId"],
      },
    },
    handler: async (args) =>
      runWithComment(args, (comment) => {
        comment.delete();
      }),
  },

  {
    minRequirementSet: "1.4", // iterate + Comment.delete (avoids desktop-only deleteAllComments)
    schema: {
      name: "delete_all_comments",
      description: "Delete every comment in the document. This is permanent.",
      parameters: { type: "object", properties: {} },
    },
    handler: async () => {
      return Word.run(async (context) => {
        // Bulk deletion (a forEach loop or native Document.deleteAllComments) either
        // no-ops or throws GeneralException on this host. Only the single-delete path
        // (findCommentById → delete → sync) persists reliably, so reuse it per id.
        const comments = context.document.body.getComments();
        comments.load("items/id");
        await context.sync();
        const ids = comments.items.map((c) => c.id);

        let deletedCount = 0;
        for (const id of ids) {
          const comment = await findCommentById(context, id);
          if (comment) {
            comment.delete();
            await context.sync();
            deletedCount++;
          }
        }

        // Self-verify against the live document.
        const check = context.document.body.getComments();
        check.load("items/id");
        await context.sync();
        return { success: true, deletedCount, remainingAfter: check.items.length };
      });
    },
  },

  // ─── Structure & navigation (WT7) ─────────────────────────────────────────

  {
    minRequirementSet: "1.1", // Range.select
    schema: {
      name: "select_text",
      description:
        "Find the first occurrence of a phrase and select it (moves the user's cursor / highlights it in the document). Useful to point the user at a location. Supports the same match options as search_text.",
      parameters: {
        type: "object",
        properties: {
          query: { type: "string", description: "The text (or wildcard pattern) to find and select." },
          matchCase: { type: "boolean", description: "Match case. Defaults to false." },
          matchWholeWord: { type: "boolean", description: "Match whole words only. Defaults to false." },
          matchWildcards: { type: "boolean", description: "Treat 'query' as a wildcard pattern. Defaults to false." },
        },
        required: ["query"],
      },
    },
    handler: async (args) => {
      return Word.run(async (context) => {
        const results = context.document.body.search(args.query as string, buildSearchOptions(args));
        results.load("items");
        await context.sync();
        if (results.items.length === 0) {
          return { matched: false, error: `No match found for "${args.query}".` };
        }
        results.items[0].select();
        await context.sync();
        return { success: true, matched: true, count: results.items.length };
      });
    },
  },

  {
    minRequirementSet: "1.4", // Range.insertBookmark
    schema: {
      name: "insert_bookmark",
      description:
        "Create a named bookmark on the current selection. The name must start with a letter and contain only letters, digits, or underscores. Link to it later with insert_hyperlink '#name'.",
      parameters: {
        type: "object",
        properties: {
          name: { type: "string", description: "Bookmark name (letters/digits/underscores, starts with a letter, ≤40 chars)." },
        },
        required: ["name"],
      },
    },
    handler: async (args) => {
      const { name, error } = validateBookmarkName(args.name);
      if (error) return { error };
      return Word.run(async (context) => {
        context.document.getSelection().insertBookmark(name as string);
        await context.sync();
        return { success: true, name };
      });
    },
  },

  {
    minRequirementSet: "1.4", // Range.getBookmarks
    schema: {
      name: "get_bookmarks",
      description: "List the names of all bookmarks in the document.",
      parameters: { type: "object", properties: {} },
    },
    handler: async () => {
      return Word.run(async (context) => {
        const bookmarks = context.document.body.getRange().getBookmarks(true, true);
        await context.sync();
        return { bookmarks: bookmarks.value, count: bookmarks.value.length };
      });
    },
  },

  {
    minRequirementSet: "1.5", // Range.insertFootnote
    schema: {
      name: "insert_footnote",
      description: "Insert a footnote anchored at the current selection, with the given note text.",
      parameters: {
        type: "object",
        properties: {
          text: { type: "string", description: "The footnote text." },
        },
        required: ["text"],
      },
    },
    handler: async (args) => {
      return Word.run(async (context) => {
        context.document.getSelection().insertFootnote((args.text as string) ?? "");
        await context.sync();
        return { success: true };
      });
    },
  },

  {
    minRequirementSet: "1.5", // Range.insertEndnote
    schema: {
      name: "insert_endnote",
      description: "Insert an endnote anchored at the current selection, with the given note text.",
      parameters: {
        type: "object",
        properties: {
          text: { type: "string", description: "The endnote text." },
        },
        required: ["text"],
      },
    },
    handler: async (args) => {
      return Word.run(async (context) => {
        context.document.getSelection().insertEndnote((args.text as string) ?? "");
        await context.sync();
        return { success: true };
      });
    },
  },

  {
    minRequirementSet: "1.1", // Section.getHeader
    schema: {
      name: "set_header",
      description:
        "Set the page header text (or HTML) for the first section. Replaces any existing header content of that type. 'type': primary (default), firstPage, evenPages.",
      parameters: {
        type: "object",
        properties: {
          text: { type: "string", description: "Plain header text. Ignored if 'html' is given." },
          html: { type: "string", description: "Optional HTML for the header (takes precedence over 'text')." },
          type: {
            type: "string",
            enum: ["primary", "firstPage", "evenPages"],
            description: "Which header to set. Defaults to primary.",
          },
        },
      },
    },
    handler: async (args) => setHeaderFooter(args, "header"),
  },

  {
    minRequirementSet: "1.1", // Section.getFooter
    schema: {
      name: "set_footer",
      description:
        "Set the page footer text (or HTML) for the first section. Replaces any existing footer content of that type. 'type': primary (default), firstPage, evenPages.",
      parameters: {
        type: "object",
        properties: {
          text: { type: "string", description: "Plain footer text. Ignored if 'html' is given." },
          html: { type: "string", description: "Optional HTML for the footer (takes precedence over 'text')." },
          type: {
            type: "string",
            enum: ["primary", "firstPage", "evenPages"],
            description: "Which footer to set. Defaults to primary.",
          },
        },
      },
    },
    handler: async (args) => setHeaderFooter(args, "footer"),
  },

  // ─── Track changes / revisions (WT6) ──────────────────────────────────────

  {
    minRequirementSet: "1.4", // Document.changeTrackingMode
    schema: {
      name: "set_track_changes",
      description:
        "Turn revision tracking (track changes) on or off for the document. 'mode': 'off' stops tracking, 'trackAll' tracks everyone's edits, 'trackMineOnly' tracks only your edits. This ONLY toggles whether new edits are tracked — it does not accept or reject existing tracked changes. To accept or reject existing revisions, use accept_all_revisions / reject_all_revisions if they are available (they need a newer Word host than this toggle does).",
      parameters: {
        type: "object",
        properties: {
          mode: {
            type: "string",
            enum: ["off", "trackAll", "trackMineOnly"],
            description: "Tracking mode: 'off', 'trackAll' (everyone), or 'trackMineOnly' (your edits).",
          },
        },
        required: ["mode"],
      },
    },
    handler: async (args) => {
      const { mode, error } = resolveChangeTrackingMode(args.mode);
      if (error) return { error };
      return Word.run(async (context) => {
        const doc = context.document;
        doc.changeTrackingMode = mode as ChangeTrackingModeLiteral;
        // Same-run re-read so the returned mode reflects what actually persisted.
        doc.load("changeTrackingMode");
        await context.sync();
        return { success: true, mode: doc.changeTrackingMode };
      });
    },
  },

  {
    minRequirementSet: "1.6", // Body.getTrackedChanges
    schema: {
      name: "get_tracked_changes",
      description:
        "List the tracked changes (revisions) in the document — each with its type ('Added' / 'Deleted' / 'Formatted'), author, date, and text. Read-only: it does not accept or reject anything. Returns at most 'limit' changes (default 200) to bound the response; 'count' is the true total and 'truncated' is true when more exist than were returned.",
      parameters: {
        type: "object",
        properties: {
          limit: {
            type: "number",
            description: "Max changes to return (default 200). 'count' still reports the true total.",
          },
        },
      },
    },
    handler: async (args) => {
      const limit =
        typeof args.limit === "number" && Number.isInteger(args.limit) && args.limit > 0
          ? args.limit
          : TRACKED_CHANGES_DEFAULT_LIMIT;
      return Word.run(async (context) => {
        const changes = context.document.body.getTrackedChanges();
        changes.load("items/type,items/author,items/date,items/text");
        await context.sync();
        const total = changes.items.length;
        return {
          count: total,
          truncated: total > limit,
          changes: changes.items.slice(0, limit).map((c) => mapTrackedChange(c)),
        };
      });
    },
  },

  {
    minRequirementSet: "1.6", // TrackedChange.accept (per-change, one op per sync)
    schema: {
      name: "accept_all_revisions",
      description:
        "Accept ALL tracked changes in the document (every revision, not just those currently shown or filtered in the review pane). This is permanent and cannot be undone from the assistant. Does not change whether tracking is on — use set_track_changes for that.",
      parameters: { type: "object", properties: {} },
    },
    handler: async () => {
      return Word.run(async (context) => {
        const { processed, remaining, error } = await acceptOrRejectAllTrackedChanges(context, "accept");
        return { success: remaining === 0 && !error, acceptedCount: processed, remaining, ...(error ? { error } : {}) };
      });
    },
  },

  {
    minRequirementSet: "1.6", // TrackedChange.reject (per-change, one op per sync)
    schema: {
      name: "reject_all_revisions",
      description:
        "Reject ALL tracked changes in the document (every revision, not just those currently shown or filtered in the review pane). This is permanent and cannot be undone from the assistant. Does not change whether tracking is on — use set_track_changes for that.",
      parameters: { type: "object", properties: {} },
    },
    handler: async () => {
      return Word.run(async (context) => {
        const { processed, remaining, error } = await acceptOrRejectAllTrackedChanges(context, "reject");
        return { success: remaining === 0 && !error, rejectedCount: processed, remaining, ...(error ? { error } : {}) };
      });
    },
  },
];

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

/**
 * Pure capability gate: keep every untagged tool, plus any tool whose
 * `minRequirementSet` the host supports per the given predicate. No live Office
 * dependency — pass a mocked `isSetSupported` to unit-test it.
 */
export function filterSupportedTools(
  candidateTools: WordTool[],
  isSetSupported: IsSetSupported
): WordTool[] {
  return candidateTools.filter(
    (t) =>
      t.minRequirementSet === undefined ||
      isSetSupported("WordApi", t.minRequirementSet)
  );
}

/**
 * Map of tool name → the requirement set it needs. Used by `handleWordToolCall`
 * to return a structured gated-error (instead of throwing) when a tool is
 * invoked on a host that can't run it.
 */
const requiredSetByTool: Map<string, string> = new Map(
  tools
    .filter((t) => t.minRequirementSet !== undefined)
    .map((t) => [t.schema.name, t.minRequirementSet as string])
);

/**
 * Build the schemas to advertise to the AI, gated to what the running host
 * supports. Defaults to the live Office predicate; callers in a non-Office
 * context (tests) pass their own. MUST be called after `Office.onReady` — the
 * requirements API is only reliable once Office has initialised (see index.tsx).
 */
export function getSupportedWordToolSchemas(
  isSetSupported: IsSetSupported = defaultIsSetSupported
): ParentToolSchema[] {
  return filterSupportedTools(tools, isSetSupported).map((t) => t.schema);
}

/**
 * Live requirement-set probe. Guarded so it degrades to "supported" if the
 * requirements API is somehow unavailable — never gate a tool out by accident.
 */
function defaultIsSetSupported(name: string, minVersion?: string): boolean {
  try {
    return Office.context.requirements.isSetSupported(name, minVersion);
  } catch {
    return true;
  }
}

/**
 * Eager, ungated schema list (all tools). Retained for back-compat with callers
 * that bootstrap before Office is ready; prefer `getSupportedWordToolSchemas()`.
 */
export const wordToolSchemas: ParentToolSchema[] = tools.map((t) => t.schema);

const handlerMap: Map<string, WordToolHandler> = new Map(
  tools.map((t) => [t.schema.name, t.handler])
);

// Tools whose Office.run() legitimately runs longer than the 15s
// DEFAULT_TOOL_TIMEOUT_MS and must not be killed mid-operation (Office.js has no
// cancellation — a timed-out write half-completes in the background, so a wrong
// cap reintroduces the diverged-state timeout it set out to fix). Two shapes
// need the long cap:
//
//   (a) A single large write that Office lays out synchronously. A full-document
//       insert_html (a multi-page report in one call) routinely exceeds 15s on
//       macOS/WKWebView. When it timed out, the model saw a failure and RETRIED
//       under a new requestId — which dedup (keyed on requestId) can't collapse —
//       so the whole body was inserted twice. This is the duplicated-document bug
//       from the Word stress test; the long cap is the fix. insert_text /
//       insert_image / set_header / set_footer share the same large-payload risk.
//   (b) A loop of context.sync() per item, unbounded by document content:
//       - delete_all_comments: one sync PER comment (unbounded by comment count)
//       - replace_text: an insert per match (unbounded by match count)
//       - apply_bullets / apply_numbering: list-build over every targeted paragraph
//
// Mirrors Excel (write_range / create_table / add_chart) and PowerPoint
// (compose_deck etc.), which already opt their big writers into the long cap —
// Word was the one host that hadn't, which is exactly the drift this repo exists
// to kill: any tool whose run does a big single write, or loops a sync per item,
// belongs here.
const TOOL_TIMEOUT_OVERRIDES: Map<string, number> = new Map([
  ["insert_html", LONG_TOOL_TIMEOUT_MS],
  ["insert_text", LONG_TOOL_TIMEOUT_MS],
  ["insert_image", LONG_TOOL_TIMEOUT_MS],
  ["set_header", LONG_TOOL_TIMEOUT_MS],
  ["set_footer", LONG_TOOL_TIMEOUT_MS],
  ["delete_all_comments", LONG_TOOL_TIMEOUT_MS],
  ["replace_text", LONG_TOOL_TIMEOUT_MS],
  ["apply_bullets", LONG_TOOL_TIMEOUT_MS],
  ["apply_numbering", LONG_TOOL_TIMEOUT_MS],
]);

export async function handleWordToolCall(
  toolName: string,
  args: unknown,
  requestId?: string
): Promise<unknown> {
  const handler = handlerMap.get(toolName);
  if (!handler) {
    throw new Error(`Unknown Word tool: "${toolName}"`);
  }

  // Capability gate: a gated tool can still be invoked (e.g. a stale schema, or
  // a model that hallucinates it). Fail soft with a structured error the AI can
  // read, rather than crashing on an opaque Office API exception.
  const requiredSet = requiredSetByTool.get(toolName);
  if (requiredSet !== undefined && !defaultIsSetSupported("WordApi", requiredSet)) {
    return {
      error: `Word tool "${toolName}" requires WordApi ${requiredSet}, which this host does not support.`,
      requiredSet,
    };
  }

  // Serialize + dedup + timeout the Word.run() through the shared execution
  // layer. Word.run() contexts cannot run concurrently — the embed dispatches a
  // turn's tool calls all at once, so without this a slow/hung call wedges the
  // whole batch and they time out (`pending_parent_response`). See
  // @usable/office-embed tool-execution.
  return executeToolCall(
    toolName,
    args,
    () => handler(args as Record<string, unknown>),
    { requestId, timeoutMs: TOOL_TIMEOUT_OVERRIDES.get(toolName) }
  );
}

/** Test-only: clear the shared execution-layer dedup + chain state between cases. */
export function __resetWordDedupForTests(): void {
  __resetToolExecutionForTests();
}
